import { createReducer, on } from "@ngrx/store";
import { initialSubscriptionsPlusState } from "./subscriptions-plus.state";
import { UserActions } from "../../common/state/user/user.actions";
import { LicenseV2Actions } from "./subscriptions-plus.actions";
import { createDefaultLoadable, onLoadableError, onLoadableLoad, onLoadableSuccess, toLoadableWrapper } from "../../common/models";
import { ILicenseUsageEntry } from "./subscriptions-plus.state";
import { arrayToILoadableWrapperArray } from "../../common/models/loadable";
import { addLicense, addLicenses, replaceLicense, removeLicenses, setLicensesDeleting, revertLicensesLoading, applyTransitionResults, clearLicenses } from "./helpers";

export const licenseV2Reducer = createReducer(
    initialSubscriptionsPlusState,

    on(LicenseV2Actions.loadLicenses, (state, { includeChildren }) => ({
        ...state,
        includeChildren: includeChildren ?? state.includeChildren,
        licenses: onLoadableLoad({ ...state.licenses })
    })),
    on(LicenseV2Actions.loadLicensesSuccess, (state, { licenses }) => ({
        ...state,
        licenses: onLoadableSuccess({
            ...state.licenses,
            items: arrayToILoadableWrapperArray(licenses)
        })
    })),
    on(LicenseV2Actions.loadLicensesFailure, (state, { error }) => ({
        ...state,
        licenses: onLoadableError({ ...state.licenses }, error)
    })),

    on(LicenseV2Actions.getLicenseById, state => ({
        ...state,
        selectedLicense: null
    })),
    on(LicenseV2Actions.getLicenseByIdSuccess, (state, { license }) => ({
        ...state,
        selectedLicense: onLoadableSuccess(toLoadableWrapper(license))
    })),
    on(LicenseV2Actions.getLicenseByIdFailure, (state, error) => ({
        ...state,
        selectedLicense: onLoadableError(toLoadableWrapper(state.selectedLicense), error)
    })),

    on(LicenseV2Actions.addLicense, state => ({
        ...state,
        licenses: onLoadableLoad({ ...state.licenses })
    })),
    on(LicenseV2Actions.addLicenseSuccess, (state, { licenses }) => ({
        ...state,
        licenses: onLoadableSuccess({
            ...state.licenses,
            items: addLicenses(state.licenses.items, licenses)
        }),
    })),
    on(LicenseV2Actions.addLicenseFailure, (state, { error }) => ({
        ...state,
        licenses: onLoadableError({ ...state.licenses }, error)
    })),

    on(LicenseV2Actions.updateLicense, state => ({
        ...state,
        licenses: onLoadableLoad({ ...state.licenses })
    })),
    on(LicenseV2Actions.updateLicenseSuccess, (state, { license }) => ({
        ...state,
        licenses: onLoadableSuccess({
            ...state.licenses,
            items: replaceLicense(state.licenses.items, license)
        }),
        selectedLicense: onLoadableSuccess(toLoadableWrapper(license))
    })),
    on(LicenseV2Actions.updateLicenseFailure, (state, { error }) => ({
        ...state,
        licenses: onLoadableError(toLoadableWrapper({ ...state.licenses }), error)
    })),

    on(LicenseV2Actions.deleteLicenses, (state, { ids }) => ({
        ...state,
        licenses: {
            ...onLoadableLoad({ ...state.licenses }),
            items: setLicensesDeleting(state.licenses.items, ids)
        }
    })),
    on(LicenseV2Actions.deleteLicensesSuccess, (state, { licenses }) => ({
        ...state,
        licenses: onLoadableSuccess({
            ...state.licenses,
            items: removeLicenses(state.licenses.items, licenses)
        })
    })),
    on(LicenseV2Actions.deleteLicensesFailure, (state, { error }) => ({
        ...state,
        licenses: onLoadableError({ ...state.licenses }, error)
    })),

    on(LicenseV2Actions.bulkTransition, (state) => ({
        ...state,
        transition: onLoadableLoad({ ...state.transition }),
    })),
    on(LicenseV2Actions.bulkTransitionSuccess, (state, { response }) => ({
        ...state,
        transition: onLoadableSuccess({ ...state.transition }),
        licenses: {
            ...state.licenses,
            items: applyTransitionResults(state.licenses.items, response)
        }
    })),
    on(LicenseV2Actions.bulkTransitionFailure, (state, { error }) => ({
        ...state,
        transition: onLoadableError({ ...state.transition }, error),
        licenses: {
            ...state.licenses,
            items: revertLicensesLoading(state.licenses.items)
        }
    })),

    on(LicenseV2Actions.bulkEditLicenses, (state) => ({
        ...state,
        bulkEdit: { ...onLoadableLoad({ ...state.bulkEdit }), response: null },
    })),
    on(LicenseV2Actions.bulkEditLicensesSuccess, (state, { response }) => ({
        ...state,
        bulkEdit: { ...onLoadableSuccess({ ...state.bulkEdit }), response },
    })),
    on(LicenseV2Actions.bulkEditLicensesFailure, (state, { error }) => ({
        ...state,
        bulkEdit: onLoadableError({ ...state.bulkEdit }, error),
    })),
    on(LicenseV2Actions.resetBulkEdit, (state) => ({
        ...state,
        bulkEdit: { ...initialSubscriptionsPlusState.bulkEdit },
    })),

    on(LicenseV2Actions.loadLicenseUsage, (state, { licenseId }) => {
        const existing = state.usage.byLicenseId[licenseId] ?? { ...createDefaultLoadable(), items: [] };
        return {
            ...state,
            usage: {
                byLicenseId: {
                    ...state.usage.byLicenseId,
                    [licenseId]: onLoadableLoad<ILicenseUsageEntry>({ ...existing, items: [] }),
                },
            },
        };
    }),
    on(LicenseV2Actions.loadLicenseUsageSuccess, (state, { licenseId, usage }) => {
        const existing = state.usage.byLicenseId[licenseId] ?? { ...createDefaultLoadable(), items: [] };
        return {
            ...state,
            usage: {
                byLicenseId: {
                    ...state.usage.byLicenseId,
                    [licenseId]: onLoadableSuccess<ILicenseUsageEntry>({ ...existing, items: usage }),
                },
            },
        };
    }),
    on(LicenseV2Actions.loadLicenseUsageFailure, (state, { licenseId, error }) => {
        const existing = state.usage.byLicenseId[licenseId] ?? { ...createDefaultLoadable(), items: [] };
        return {
            ...state,
            usage: {
                byLicenseId: {
                    ...state.usage.byLicenseId,
                    [licenseId]: onLoadableError<ILicenseUsageEntry>({ ...existing, items: [] }, error),
                },
            },
        };
    }),
    on(LicenseV2Actions.clearLicenseUsage, (state, { licenseId }) => {
        const { [licenseId]: _removed, ...rest } = state.usage.byLicenseId;
        return {
            ...state,
            usage: { byLicenseId: rest },
        };
    }),

    on(LicenseV2Actions.distributeLicenses, state => ({
        ...state,
        distribution: onLoadableLoad({ ...state.distribution })
    })),
    on(LicenseV2Actions.distributeLicensesSuccess, (state, { response }) => {
        const newLicenses = response.results
            .filter(r => r.status === 'success')
            .flatMap(r => r.data);
        return {
            ...state,
            distribution: onLoadableSuccess({ ...state.distribution }),
            licenses: {
                ...state.licenses,
                items: addLicenses(state.licenses.items, newLicenses)
            }
        };
    }),
    on(LicenseV2Actions.distributeLicensesFailure, (state, { error }) => ({
        ...state,
        distribution: onLoadableError({ ...state.distribution }, error)
    })),

    on(LicenseV2Actions.loadLicenseLinks, state => ({
        ...state,
        licenseLinks: onLoadableLoad({ ...state.licenseLinks })
    })),
    on(LicenseV2Actions.loadLicenseLinksSuccess, (state, { linksByLicense }) => {
        const linkMap = new Map<string, Set<string>>();
        for (const entry of linksByLicense) {
            linkMap.set(entry.licenseId, new Set(entry.links.map(l => l.entityId)));
        }
        return {
            ...state,
            licenseLinks: onLoadableSuccess({
                ...state.licenseLinks,
                linksByLicense: linkMap,
            })
        };
    }),
    on(LicenseV2Actions.loadLicenseLinksFailure, (state, { error }) => ({
        ...state,
        licenseLinks: onLoadableError({ ...state.licenseLinks }, error)
    })),

    on(LicenseV2Actions.loadGlobalLinks, state => ({
        ...state,
        globalLinks: onLoadableLoad({ ...state.globalLinks })
    })),
    on(LicenseV2Actions.loadGlobalLinksSuccess, (state, { linksByLicense }) => {
        const linkedCameraIds = new Set<string>();
        const cameraToLicense = new Map<string, string>();
        for (const entry of linksByLicense) {
            for (const link of entry.links) {
                if (link.entityType === 'camera') {
                    linkedCameraIds.add(link.entityId);
                    cameraToLicense.set(link.entityId, entry.licenseId);
                }
            }
        }
        return {
            ...state,
            globalLinks: onLoadableSuccess({
                ...state.globalLinks,
                linkedCameraIds,
                cameraToLicense,
            })
        };
    }),
    on(LicenseV2Actions.loadGlobalLinksFailure, (state, { error }) => ({
        ...state,
        globalLinks: onLoadableError({ ...state.globalLinks }, error)
    })),

    on(LicenseV2Actions.loadLinksOverview, state => ({
        ...state,
        linksOverview: onLoadableLoad({ ...state.linksOverview })
    })),
    on(LicenseV2Actions.loadLinksOverviewSuccess, (state, { linksByLicense }) => {
        // Merged (not replaced) so a single-license fetch from the linked-entities
        // dialog doesn't discard the page-wide overview loaded by the license list.
        const merged = new Map(state.linksOverview.linksByLicense);
        for (const entry of linksByLicense) {
            merged.set(entry.licenseId, entry.links);
        }
        return {
            ...state,
            linksOverview: onLoadableSuccess({
                ...state.linksOverview,
                linksByLicense: merged,
            })
        };
    }),
    on(LicenseV2Actions.loadLinksOverviewFailure, (state, { error }) => ({
        ...state,
        linksOverview: onLoadableError({ ...state.linksOverview }, error)
    })),

    on(LicenseV2Actions.bulkManageLinks, state => ({
        ...state,
        linkManagement: onLoadableLoad({ ...state.linkManagement })
    })),
    on(LicenseV2Actions.bulkManageLinksSuccess, (state, { response }) => ({
        ...state,
        linkManagement: onLoadableSuccess({ ...state.linkManagement, response })
    })),
    on(LicenseV2Actions.bulkManageLinksFailure, (state, { error }) => ({
        ...state,
        linkManagement: onLoadableError({ ...state.linkManagement }, error)
    })),

    on(LicenseV2Actions.clearLinkManagement, state => ({
        ...state,
        linkManagement: { ...initialSubscriptionsPlusState.linkManagement },
        licenseLinks: { ...initialSubscriptionsPlusState.licenseLinks },
        globalLinks: { ...initialSubscriptionsPlusState.globalLinks },
    })),

    on(LicenseV2Actions.loadLinkEntityData, state => ({
        ...state,
        linkEntity: onLoadableLoad({ ...state.linkEntity })
    })),
    on(LicenseV2Actions.loadLinkEntityDataSuccess, (state, { currentlyLinkedOrgId, orgOptions }) => ({
        ...state,
        linkEntity: onLoadableSuccess({ ...state.linkEntity, currentlyLinkedOrgId, orgOptions })
    })),
    on(LicenseV2Actions.loadLinkEntityDataFailure, (state, { error }) => ({
        ...state,
        linkEntity: onLoadableError({ ...state.linkEntity }, error)
    })),
    on(LicenseV2Actions.clearLinkEntityData, state => ({
        ...state,
        linkEntity: { ...initialSubscriptionsPlusState.linkEntity },
    })),

    on(LicenseV2Actions.loadLicenseNotes, (state, { licenseId }) => ({
        ...state,
        notes: {
            ...onLoadableLoad({ ...state.notes }),
            licenseId,
            items: [],
        }
    })),
    on(LicenseV2Actions.loadLicenseNotesSuccess, (state, { licenseId, notes }) => ({
        ...state,
        notes: {
            ...onLoadableSuccess({ ...state.notes }),
            licenseId,
            items: notes,
        }
    })),
    on(LicenseV2Actions.loadLicenseNotesFailure, (state, { error }) => ({
        ...state,
        notes: {
            ...onLoadableError({ ...state.notes }, error),
            licenseId: state.notes.licenseId,
            items: [],
        }
    })),
    on(LicenseV2Actions.clearLicenseNotes, state => ({
        ...state,
        notes: { ...initialSubscriptionsPlusState.notes },
        noteCreate: { ...initialSubscriptionsPlusState.noteCreate },
        noteDelete: { ...initialSubscriptionsPlusState.noteDelete },
    })),

    on(LicenseV2Actions.resetNoteCreate, state => ({
        ...state,
        noteCreate: { ...initialSubscriptionsPlusState.noteCreate },
    })),

    on(LicenseV2Actions.createLicenseNote, state => ({
        ...state,
        noteCreate: onLoadableLoad({ ...state.noteCreate }),
    })),
    on(LicenseV2Actions.createLicenseNoteSuccess, (state, { note }) => ({
        ...state,
        noteCreate: onLoadableSuccess({ ...state.noteCreate }),
        notes: {
            ...state.notes,
            items: [note, ...state.notes.items],
        }
    })),
    on(LicenseV2Actions.createLicenseNoteFailure, (state, { error }) => ({
        ...state,
        noteCreate: onLoadableError({ ...state.noteCreate }, error),
    })),

    on(LicenseV2Actions.deleteLicenseNote, state => ({
        ...state,
        noteDelete: onLoadableLoad({ ...state.noteDelete }),
    })),
    on(LicenseV2Actions.deleteLicenseNoteSuccess, (state, { noteId }) => ({
        ...state,
        noteDelete: onLoadableSuccess({ ...state.noteDelete }),
        notes: {
            ...state.notes,
            items: state.notes.items.filter(n => n.id !== noteId),
        }
    })),
    on(LicenseV2Actions.deleteLicenseNoteFailure, (state, { error }) => ({
        ...state,
        noteDelete: onLoadableError({ ...state.noteDelete }, error),
    })),

    on(LicenseV2Actions.loadChildOrganisations, state => ({
        ...state,
        childOrganisations: onLoadableLoad({ ...state.childOrganisations }),
    })),
    on(LicenseV2Actions.loadChildOrganisationsSuccess, (state, { childOrgs }) => ({
        ...state,
        childOrganisations: onLoadableSuccess({
            ...state.childOrganisations,
            items: childOrgs,
        }),
    })),
    on(LicenseV2Actions.loadChildOrganisationsFailure, (state, { error }) => ({
        ...state,
        childOrganisations: onLoadableError({ ...state.childOrganisations }, error),
    })),
    on(LicenseV2Actions.clearChildOrganisations, state => ({
        ...state,
        childOrganisations: { ...initialSubscriptionsPlusState.childOrganisations },
    })),

    on(LicenseV2Actions.clearLicenses, _ => ({ ...initialSubscriptionsPlusState })),
    on(UserActions.logout, _ => ({ ...initialSubscriptionsPlusState })),
);
